import { z } from "zod";
import type { OrganizationTypeRefWithLabel } from "./organization-types";
import { paginationQuery, searchParam } from "./pagination";

/**
 * `/responsible-organizations` — the org that owns a registration: an NOC, the
 * host organising committee, a broadcaster, a technical-officials pool. Wire
 * shapes only (snake_case), as the API's `ok()` emits them; see CLAUDE.md
 * § Naming.
 *
 * `country_id` is nullable ON PURPOSE — "NOC Malaysia" has a country, the host
 * committee and "ABC Broadcasting" do not. Nationality lives on `people`,
 * never derived through this table.
 *
 * Since 0016 an organisation's kind is a row of `organization_types` (NOC,
 * HOST, IF, MEDIA, OTHER — admin-editable), referenced by
 * `organization_type_id`. It drives the Register chain: which categories and
 * which sub-agencies the organisation picks from. The old free-text `type`
 * column is no longer read or written by the API (dropped by a later
 * migration — "Known traps" 11).
 */

/**
 * `organization_type_id` as a LIST filter: a type's uuid, or the literal
 * `none` for rows whose type is not set yet (`organization_type_id IS NULL`).
 */
export const ORGANIZATION_TYPE_NONE = "none" as const;
export const organizationTypeFilterParam = z.string().uuid().or(z.literal(ORGANIZATION_TYPE_NONE));

/**
 * One bucket of a `/stats` breakdown by organisation type. The bucket for rows
 * with no type has `organization_type_id`, `code` and `name` all `null`.
 */
export interface OrganizationTypeCount {
	organization_type_id: string | null;
	code: string | null;
	name: string | null;
	count: number;
}

export const RESPONSIBLE_ORGANIZATION_SORT_KEYS = ["name", "organization_type", "created_at"] as const;
export type ResponsibleOrganizationSortKey = (typeof RESPONSIBLE_ORGANIZATION_SORT_KEYS)[number];

export const listResponsibleOrganizationsQuery = paginationQuery.extend({
	search: searchParam.optional(),
	organization_type_id: organizationTypeFilterParam.optional(),
	country_id: z.string().uuid().optional(),
	sort: z.enum(RESPONSIBLE_ORGANIZATION_SORT_KEYS).optional(),
	dir: z.enum(["asc", "desc"]).optional(),
});
export type ListResponsibleOrganizationsQuery = z.infer<typeof listResponsibleOrganizationsQuery>;

/** `GET /responsible-organizations/options?organization_type_id=` — step 1 of the Register chain. */
export const responsibleOrganizationOptionsQuery = z.object({
	organization_type_id: z.string().uuid().optional(),
});
export type ResponsibleOrganizationOptionsQuery = z.infer<typeof responsibleOrganizationOptionsQuery>;

/** One row of `GET /responsible-organizations`. */
export interface ResponsibleOrganizationListItem {
	responsible_organization_id: string;
	name: string;
	/** `null` = not set yet. */
	organization_type: OrganizationTypeRefWithLabel | null;
	country_id: string | null;
	country_name: string | null;
	/** Cards issued under this org. An org in use cannot be deleted. */
	cards_count: number;
	/** User-group scope rows (`user_group_scopes`) naming this org — groups whose visibility hangs off it. */
	scopes_count: number;
	created_at: string;
	updated_at: string;
}

/** `GET /responsible-organizations/options` — the whole list, for `<select>` elements. */
export interface ResponsibleOrganizationOption {
	responsible_organization_id: string;
	name: string;
	organization_type: OrganizationTypeRefWithLabel | null;
	country_id: string | null;
}

/** `GET /responsible-organizations/stats` — the stats strip, in one statement. */
export interface ResponsibleOrganizationStats {
	total: number;
	with_country: number;
	without_country: number;
	/** Every organisation type (zero counts included), then the no-type bucket. */
	by_organization_type: OrganizationTypeCount[];
}

// ── Write requests ──────────────────────────────────────────────────────────

export interface ResponsibleOrganizationFormMessages {
	nameRequired: string;
}

export const enResponsibleOrganizationFormMessages: ResponsibleOrganizationFormMessages = {
	nameRequired: "Enter a name",
};

/**
 * Optional selects bind as `""`; the `to*Body` helpers drop or null them.
 * `organization_type_id` is optional in the FORM too: a form that does not
 * render it leaves it `undefined`, and the update helper then leaves the
 * column alone instead of clearing it.
 */
const responsibleOrganizationFields = (m: ResponsibleOrganizationFormMessages) => ({
	name: z.string().trim().min(1, m.nameRequired).max(200),
	organization_type_id: z.string().uuid().or(z.literal("")).optional(),
	country_id: z.string().uuid().or(z.literal("")),
});

export function makeResponsibleOrganizationForm(m: ResponsibleOrganizationFormMessages = enResponsibleOrganizationFormMessages) {
	return z.object(responsibleOrganizationFields(m));
}
export const responsibleOrganizationForm = makeResponsibleOrganizationForm();
export type ResponsibleOrganizationForm = z.infer<typeof responsibleOrganizationForm>;

export interface CreateResponsibleOrganizationBody {
	name: string;
	organization_type_id?: string;
	country_id?: string;
}

/** `null` clears a field; omitted leaves it alone. The two must stay distinguishable. */
export interface UpdateResponsibleOrganizationBody {
	name?: string;
	organization_type_id?: string | null;
	country_id?: string | null;
	/** Headline for this change in the Activity feed. */
	reason?: string;
}

export const toCreateResponsibleOrganizationBody = (f: ResponsibleOrganizationForm): CreateResponsibleOrganizationBody => ({
	name: f.name,
	...(f.organization_type_id ? { organization_type_id: f.organization_type_id } : {}),
	...(f.country_id ? { country_id: f.country_id } : {}),
});

/**
 * Only what actually changed, so the audit row's `changed_fields` names the
 * edit. A field the admin cleared is sent as an explicit `null`; one they left
 * alone is omitted entirely.
 */
export function toUpdateResponsibleOrganizationBody(f: ResponsibleOrganizationForm, before: ResponsibleOrganizationListItem): UpdateResponsibleOrganizationBody {
	const body: UpdateResponsibleOrganizationBody = {};
	if (f.name !== before.name) body.name = f.name;
	const diffNullable = (next: string, prev: string | null) => (next === "" ? (prev === null ? undefined : null) : next === prev ? undefined : next);
	if (f.organization_type_id !== undefined) {
		const type = diffNullable(f.organization_type_id, before.organization_type?.id ?? null);
		if (type !== undefined) body.organization_type_id = type;
	}
	const country = diffNullable(f.country_id, before.country_id);
	if (country !== undefined) body.country_id = country;
	return body;
}
